import { zodResolver } from '@hookform/resolvers/zod'
import { postSubscribeNewsletter } from 'api/main/newsletter'
import NewsletterIcon from 'assets/icons/NewsletterIcon'
import React, { useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { newsletterSchema } from 'schemas/newsletter'
import { showErrorMessage } from 'utils'
import { getErrorMessage } from 'utils/getErrorMessage'
import CustomButton from 'components/atoms/custom-button'
import ReCAPTCHA from 'react-google-recaptcha'
import { Config } from 'utils/initialConfig'
import NewsLetterSuccessModal from 'components/organisms/Modal/NewsLetterSuccessModal'
import { Input } from 'antd'

const NewsletterFooter = ({ inView }) => {
  const recaptchaRef = useRef(null)
  const [isSuccessOpen, setIsSuccessOpen] = useState(false)
  const { control, reset, handleSubmit } = useForm({
    defaultValues: {
      name: '',
      email: ''
    },
    mode: 'onChange',
    resolver: zodResolver(newsletterSchema)
  })
  async function onSubmit(data) {
    try {
      const token = await recaptchaRef.current?.executeAsync()
      if (!token) {
        showErrorMessage('Recaptcha verification failed')
        return
      }

      // kirim ke mutation
      subscribeNewsletterMutation.mutate({
        email: data.email,
        name: data?.name,
        captcha_token: token
      })
    } catch (error) {
      recaptchaRef.current?.reset()
      console.error('reCAPTCHA error:', error)
      showErrorMessage('Something went wrong with reCAPTCHA')
    }
  }

  const subscribeNewsletterMutation = useMutation({
    mutationFn: (payload) => postSubscribeNewsletter(payload),
    mutationKey: ['subscribeNewsLetterMutation'],
    onSuccess: () => {
      setIsSuccessOpen(true)
      reset()
      recaptchaRef.current?.reset()
    },
    onError: (err) => {
      recaptchaRef.current?.reset()
      showErrorMessage(getErrorMessage(err))
    }
  })
  return (
    <>
      <div className="relative grid w-full grid-cols-1 gap-4 rounded-lg border border-white/20 lg:flex lg:justify-between lg:gap-10 lg:border-solid lg:p-6">
        <div className="flex flex-1 flex-col justify-start lg:flex-row lg:justify-start">
          <NewsletterIcon className="mt-2 scale-95 fill-white/80 stroke-white/80" />
          <div className="flex flex-col justify-center">
            <h2 className="text-start text-lg font-medium text-white md:text-base lg:text-start lg:text-lg xl:text-xl">
              Insight Berharga untuk Keputusan Lebih Baik
            </h2>
            <p className="text-sm font-normal text-white/70 lg:text-sm xl:text-base">
              Dapatkan pembaruan terkurasi tentang akuisisi, tren, dan pertumbuhan bisnis.
            </p>
          </div>
        </div>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full flex-1 flex-col items-center justify-end gap-2 lg:flex-row ">
          <div className="flex w-full flex-col items-center gap-2 lg:max-w-md lg:flex-row">
            <Controller
              name={'name'}
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <div className={`relative flex w-full flex-col gap-1`}>
                    <Input
                      {...field}
                      placeholder="Nama Lengkap"
                      autoComplete="name"
                      type={'text'}
                      status={fieldState?.error?.message ? 'error' : ''}
                      name="name"
                    />
                    {fieldState?.error?.message ? (
                      <span className="top-full text-sm font-medium text-white lg:absolute">
                        {'wajib diisi'}
                      </span>
                    ) : null}
                  </div>
                )
              }}
            />
            <Controller
              name={'email'}
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <div className={`relative flex w-full flex-col gap-1`}>
                    <Input
                      {...field}
                      placeholder="Email address"
                      autoComplete="email"
                      type={'text'}
                      status={fieldState?.error?.message ? 'error' : ''}
                      name="email"
                    />
                    {fieldState?.error?.message ? (
                      <span className="top-full text-sm font-medium text-white lg:absolute">
                        {fieldState?.error?.message ?? 'wajib diisi'}
                      </span>
                    ) : null}
                  </div>
                )
              }}
            />
          </div>
          <CustomButton
            className="w-full lg:w-fit"
            htmlType="submit"
            isLoading={subscribeNewsletterMutation?.isLoading}
            type="primary-light">
            Subscribe
          </CustomButton>
        </form>
      </div>
      {inView && (
        <ReCAPTCHA
          ref={recaptchaRef}
          badge="bottomleft"
          size="invisible"
          sitekey={Config.REACT_RECAPTCHA_SITE_KEY}
        />
      )}
      {isSuccessOpen && (
        <NewsLetterSuccessModal
          isOpen={isSuccessOpen}
          onCloseModal={() => {
            setIsSuccessOpen(false)
            recaptchaRef.current?.reset()
          }}
        />
      )}
    </>
  )
}

export default NewsletterFooter
